<template>
  <div>
    <PageAllow controller="Settlements" action="getPurchaseWaitSettleBills" />
    <Breadcrumbs :items="items" />

    <v-sheet class="transparent-background-color">
      <v-row dense class="mx-5">
        <v-col cols="12" sm="12" md="4">
          <v-row dense align="center" class="pa-1">
            <SearcherDatetimeRangeWithRadio
              v-model="options"
              type="datetime"
              show-radios
              :radios="datetimeRadios"
              deleteIcon
              removeIcon
            />
          </v-row>
        </v-col>
        <v-col cols="12" sm="12" md="8" align-self="center">
          <v-row dense align="center" class="search-controls">
            <v-tooltip bottom>
              <template v-slot:activator="{ on, attrs }">
                <v-btn
                  v-bind="attrs"
                  v-on="on"
                  small
                  text
                  class="px-0 py-0 caption font-weight-bold grey--text"
                  color="primary"
                  @click.stop="onSearch()"
                >
                  <v-icon small class="pe-1">fa-search</v-icon>
                  {{ $t("Search.search") }}
                </v-btn>
              </template>
              <span>{{ $t("Search.searchTip") }}</span>
            </v-tooltip>
            <v-tooltip bottom>
              <template v-slot:activator="{ on, attrs }">
                <v-btn
                  v-bind="attrs"
                  v-on="on"
                  small
                  text
                  class="caption font-weight-bold grey--text"
                  @click.stop="resetSearch()"
                >
                  <v-icon x-small class="pe-1">fa-trash-alt</v-icon>
                  {{ $t("Search.reset") }}
                </v-btn>
              </template>
              <span>{{ $t("Search.resetTip") }}</span>
            </v-tooltip>
            <v-divider vertical />
            <v-btn
              class="caption font-weight-bold grey--text"
              :class="
                $vuetify.theme.dark ? 'text--lighten-1' : 'text--darken-1'
              "
              small
              text
              @click.stop="$store.commit('toggleSearchPanel')"
            >
              {{ $t("Search.pro") }}
              <v-icon small class="ps-1">{{
                $store.state.showSearchPanel ? "fa-angle-up" : "fa-angle-down"
              }}</v-icon>
            </v-btn>
            <v-spacer />
            <v-switch
              v-model="showSelected"
              small
              flat
              :disabled="!(bills && bills.length)"
              :label="$t('SettlementCreate.showSelect')"
            />
            <v-btn
              v-if="$permission.permissionAllow('Settlements', 'updateUnsettledBill') && showSelected"
              small
              color="primary"
              class="elevation-1 ms-1"
              :disabled="!unsettledBills.length"
              @click="confirmToAddQueueByCodeDialog = true"
            >
              {{ $t("SettlementCreate.addUnsettled", [unsettledBills ? unsettledBills.length : 0]) }}
            </v-btn>
            <v-btn
              v-if="$permission.permissionAllow('Settlements', 'addQueue') && !showSelected"
              small
              color="primary"
              class="elevation-1 ms-1"
              @click.stop="confirmToAddQueueBySearchDialog = true"
            >
              {{ $t("SettlementCreate.addQueueBySearch") }}
            </v-btn>
          </v-row>
        </v-col>
      </v-row>
      <v-expansion-panels
        v-model="$store.getters.panels"
        multiple
        flat
        accordion
      >
        <v-expansion-panel class="transparent-background-color">
          <v-expansion-panel-content>
            <v-row dense align="center">
              <v-col cols="12" sm="6" md="4">
                <SearcherStatus
                  :label="$t('Receipt.payFlgs.title')"
                  :items="$consts.financeFlgs"
                  v-model="options.pay_flgs"
                  :disable="true"
                />
              </v-col>
              <v-col cols="12" sm="6" md="4">
                <SearcherStatus
                  v-model="options.bill_types"
                  :label="$t('ReceiptBillAnalyze.billTypes.title')"
                  :items="$consts.purchaseBillTypes"
                />
              </v-col>
            </v-row>
          </v-expansion-panel-content>
        </v-expansion-panel>
      </v-expansion-panels>
      <div class="mx-4 my-4">
        <v-data-table
          v-model="selectedBill"
          :headers="headers"
          class="elevation-2 pa-4 mt-4"
          :items="bills"
          :show-select="showSelected"
          item-key="bill_code"
          disable-sort
          :options.sync="options"
          :page.sync="paging.page"
          :server-items-length="paging.count"
          :footer-props="footerProps"
          :loading="loading"
          @item-selected="onToggleSelect"
          @toggle-select-all="onToggleSelectAll"
        >
          <!-- 应收应付 -->
          <template v-slot:item.total_price="{ item, index }">
            <span class="text-no-wrap" :v-resize="!index ? $stickTableResizer() : null">
              <v-chip
                small
                label
                color="primary"
                dark
                v-if="item.io_type == 1"
              >
                {{ item.io_str }}
              </v-chip>
              <v-chip
                small
                label
                color="danger"
                dark
                v-if="item.io_type == 2"
              >
                {{ item.io_str }}
              </v-chip>
              <v-chip
                small
                label
                color="danger"
                dark
                v-if="item.io_type == 3"
              >
                {{ item.io_str }}
              </v-chip>
              {{ $t("Global.money") }}
              {{ item.total_price | formatMoney }}
            </span>
          </template>
          <!-- 待收/待付 -->
          <template v-slot:item.wait_fee="{ item }">
            <span class="text-no-wrap">
              <v-chip
                small
                label
                color="primary"
                dark
                v-if="item.wait_io_type == 1"
              >
                <span v-if="item.total_price < item.down_sum - item.up_sum">
                  {{ $t("Receipt.Fields.retreat") }}
                </span>
                <span v-else>{{ item.wait_io_str }}</span>
              </v-chip>
              <v-chip
                small
                label
                color="danger"
                dark
                v-if="item.wait_io_type == 2"
              >
                {{ item.wait_io_str }}
              </v-chip>
              <v-chip
                small
                label
                color="danger"
                dark
                v-if="item.wait_io_type == 3"
              >
                {{ item.wait_io_str }}
              </v-chip>
              {{ $t("Global.money") }}
              <!-- 当待收待付类型为退或者付时 -->
              <span v-if="item.wait_io_type == '2' || item.wait_io_type == '3'">
                {{ item.wait_pay_price | formatMoney }}
              </span>
              <span v-else>
                {{ item.wait_receive_price | formatMoney }}
              </span>
            </span>
          </template>
          <template v-slot:item.bill_check_flg="{ item }">
            <v-chip
                small
                label
                :color="item.bill_check_flg | billCheckFlgColor"
                :text-color="item.bill_check_flg | billCheckFlgTextColor"
            >
              {{ item.bill_check_flg | billCheckFlgText }}
            </v-chip>
          </template>
          <template v-slot:item.pay_flg="{ item }">
            <v-chip
              small
              label
              :color="item.pay_flg | financeFlgsColor"
              :text-color="item.pay_flg | financeFlgsTextColor"
            >
              {{ item.pay_flg | financeFlgsText }}
            </v-chip>
          </template>
          <template v-slot:item.status="{ item }">
            <v-chip
              small
              label
              :color="item.status | billFlgsColor"
              :text-color="item.status | billFlgsTextColor"
            >
              {{ item.status | billFlgsText }}
            </v-chip>
          </template>
          <template v-slot:item.created="{ item }">
            {{ item.created | formatTime }}
          </template>
          <template v-slot:item.actual_date="{ item }">
            {{ item.actual_date | formatTime }}
          </template>
          <template v-slot:item.finish_time="{ item }">
            {{ item.finish_time | formatTime }}
          </template>
        </v-data-table>
      </div>
    </v-sheet>
    <DialogAlert
      v-model="confirmToAddQueueBySearchDialog"
      :title="$t('SettlementCreate.confirm')"
      :message="$t('SettlementCreate.confirmToAddQueueBySearch')"
      useConfirm
      @confirm="addQueueBySearch()"
    />
    <DialogAlert
      v-model="confirmToAddQueueByCodeDialog"
      :title="$t('SettlementCreate.confirm')"
      :message="$t('SettlementCreate.confirmToAddQueueByCode', [unsettledBills ? unsettledBills.length : 0])"
      useConfirm
      @confirm="addQueueByCode()"
    />
  </div>
</template>

<script>
import { mapState } from "vuex";
import i18n from "@/i18n";
import PageAllow from "@/components/PageAllow";
import Breadcrumbs from "@/components/Breadcrumbs";
import Settlements from "@/api/settlements";
import SearcherDatetimeRangeWithRadio from "@/components/plugins/SearcherDatetimeRangeWithRadio";
import SearcherStatus from "@/components/plugins/SearcherStatus";
import DialogAlert from "@/components/plugins/DialogAlert";


const defaultOptions = {
  datetimeRadioSelected: "order_created",
  order_created_time_from:
    new Date(new Date().getTime() - 30 * 24 * 3600 * 1000)
      .toISOString()
      .substr(0, 10) + " 00:00:00",
  order_created_time_to: new Date().toISOString().substr(0, 10) + " 23:59:59",
  actual_time_from: "",
  actual_time_to: "",
  finish_time_from: "",
  finish_time_to: "",
  bill_types: [],
  pay_flgs: [1, 3],
  settle_type: 2,
  is_related_return: [],
};

export default {
  components: {
    DialogAlert,
    PageAllow,
    Breadcrumbs,
    SearcherDatetimeRangeWithRadio,
    SearcherStatus,
  },

  computed: {
    items: () => [
      {
        text: i18n.t("Nav.home"),
        disabled: false,
        href: "/home",
      },
      {
        text: i18n.t("Nav.settlements"),
        disabled: false,
        href: "/settlements-purchase",
      },
      {
        text: i18n.t("Nav.settlementsPurchase"),
        disabled: false,
        href: "/settlements-purchase",
      },
      {
        text: i18n.t("SettlementsPurchase.addBtn"),
        disabled: true,
        href: "/settlements-purchase",
      },
    ],
    headers() {
      var headers = [
        {
          text: i18n.t("Receipt.Fields.bill_code"),
          align: "start",
          sortable: false,
          value: "bill_code",
        },
        {
          text: i18n.t("Receipt.Fields.org_code"),
          align: "start",
          sortable: false,
          value: "org_code",
        },
        {
          text: i18n.t("Receipt.Fields.shop_name"),
          align: "start",
          sortable: false,
          value: "obj_name",
        },
        {
          text: i18n.t("Receipt.Fields.fee"),
          align: "start",
          sortable: false,
          value: "total_price",
        },
        {
          text: i18n.t("Receipt.Fields.wait_fee"),
          align: "start",
          sortable: false,
          width: "140px",
          value: "wait_fee",
        },
        {
          text: i18n.t("Receipt.Fields.bill_check_flg"),
          align: "start",
          sortable: false,
          value: "bill_check_flg",
        },
        {
          text: i18n.t("Receipt.Fields.pay_flg"),
          align: "start",
          sortable: false,
          value: "pay_flg",
        },
        {
          text: i18n.t("Receipt.Fields.status"),
          align: "start",
          sortable: false,
          value: "status",
        }
      ];
      if (this.options.datetimeRadioSelected === "order_created") {
        headers.push({
          text: i18n.t("StockOrderOUT.Fields.created_time"),
          align: "start",
          sortable: false,
          value: "created",
        });
      } else if (this.options.datetimeRadioSelected === "actual") {
        headers.push({
          text: i18n.t("StockOrderOUT.Fields.actual_time"),
          align: "start",
          sortable: false,
          value: "actual_date",
        });
      } else if (this.options.datetimeRadioSelected === "finish") {
        headers.push({
          text: i18n.t("StockOrderOUT.Fields.finish_time"),
          align: "start",
          sortable: false,
          value: "finish_time",
        });
      }
      return headers;
    },
    datetimeRadios: () => [
      {
        key: "order_created",
        text: i18n.t("StockOrderOUT.Fields.created_time"),
      },
      {
        key: "actual",
        text: i18n.t("StockOrderOUT.Fields.actual_time"),
      },
      {
        key: "finish",
        text: i18n.t("StockOrderOUT.Fields.finish_time"),
      },
    ],
    footerProps() {
      let props = Object.assign({}, i18n.t("$config.footer-props"));
      props.itemsPerPageOptions = [...props.itemsPerPageOptions, -1];
      return props;
    },
    // 使用对象展开运算符将此对象混入到外部对象中
    ...mapState(["settings"]),
  },

  data: () => ({
    bills: [],
    paging: {},
    showSelected: true,
    unsettledBills: [],
    selectedBill: [],
    options: JSON.parse(JSON.stringify(defaultOptions)),
    loading: false,
    confirmToAddQueueByCodeDialog: false,
    confirmToAddQueueBySearchDialog: false,
  }),

  mounted() {

  },

  methods: {
    resetSearch() {
      this.selectedBill = [];
      this.options = JSON.parse(JSON.stringify(defaultOptions));
    },
    onSearch() {
      this.options = Object.assign({}, this.options, {
      });
    },
    getPurchaseWaitSettleBills() {
      this.selectedBill = [];
      this.loading = true;
      let options = Object.assign({}, this.options, {
        itemsPerPage:
          this.options.itemsPerPage == -1
            ? this.$consts.limitAll
            : this.options.itemsPerPage,
      });

      Settlements.getPurchaseWaitSettleBills(options).then(
        function (response) {
          if (response.data.status.isOK()) {
            this.bills = response.data.bills;
            this.paging = response.data.paging;
            this.syncSelectedWithCache()
          } else if (response.data.error.code.isNoResultSet()) {
            this.options.page = 1;
          }
          this.loading = false;
        }.bind(this)
      );
    },
    // 同步 selectedBill 与当前表格 + 缓存
    syncSelectedWithCache() {
      this.selectedBill = this.unsettledBills.filter(cached =>
          this.bills.some(bill => bill.bill_code === cached.bill_code)
      );
    },
    addQueueByCode() {
      if (this.unsettledBills && this.unsettledBills.length) {
        this.$store.dispatch("fullscreenLoading");
        let billCodes = this.unsettledBills.map((bill) => {
          return bill.bill_code;
        });
        let postData = {
          type: 2, // 采购类型
          date_type: this.options.datetimeRadioSelected,
          bill_codes: billCodes
        }
        Settlements.addQueue(postData).then(
          function (response) {
            if (response.data.status.isOK()) {
              this.$snackbar.success(i18n.t("SettlementCreate.addSuccessMsg"));
              this.unsettledBills = [];
              this.selectedBill = [];
              this.getPurchaseWaitSettleBills()
            }
            this.$store.dispatch("fullscreenLoadingStop");
          }.bind(this)
        ).catch(
          function () {
            this.$store.dispatch("fullscreenLoadingStop");
          }.bind(this)
        );
      }
    },
    addQueueBySearch() {
      this.$store.dispatch("fullscreenLoading");
      let postData = {
        type: 2, // 采购类型
        date_type: this.options.datetimeRadioSelected,
        search: this.options
      }
      Settlements.addQueue(postData).then(
        function (response) {
          if (response.data.status.isOK()) {
            this.$snackbar.success(i18n.t("SettlementCreate.addSuccessMsg"));
            this.getPurchaseWaitSettleBills();
          }
          this.$store.dispatch("fullscreenLoadingStop");
        }.bind(this)
      ).catch(
        function () {
          this.$store.dispatch("fullscreenLoadingStop");
        }.bind(this)
      );
    },
    async updateUnsettledBill(mode, items) {
      if (!items.length) return;
      const billCodes = items.map(s => s.bill_code);
      const oldCache = [...this.unsettledBills];
      if (mode === 1) {
        items.forEach(item => {
          if (!this.unsettledBills.some(b => b.bill_code === item.bill_code))
            this.unsettledBills.push(item);
        });
      } else {
        items.forEach(item => {
          const idx = this.unsettledBills.findIndex(b => b.bill_code === item.bill_code);
          if (idx !== -1) this.unsettledBills.splice(idx, 1);
        });
      }
      this.syncSelectedWithCache();
      try {
        await Settlements.updateUnsettledBill({ type: 2, mode: mode, bill_codes: billCodes });
      } catch {
        this.unsettledBills = oldCache;
        this.syncSelectedWithCache();
      }
    },
    onToggleSelect({ item, value }) {
      this.updateUnsettledBill(value ? 1 : 2, [item]);
    },
    onToggleSelectAll({ items, value }) {
      this.updateUnsettledBill(value ? 1 : 2, items);
    },
  },

  watch: {
    options: {
      deep: true,
      handler() {
        this.getPurchaseWaitSettleBills();
      },
    },
    showSelected: {
      deep: true,
      handler() {
        if (this.showSelected) {
          this.$store.dispatch("fullscreenLoading");
          // 启用多选时获取当前缓存
          Settlements.getUnsettledBills({type:2}).then(
              function (response) {
                if (response.data.status.isOK()) {
                  this.unsettledBills = response.data.bills
                  this.syncSelectedWithCache();
                }
                this.$store.dispatch("fullscreenLoadingStop");
              }.bind(this)
          );
        } else {
          this.unsettledBills = [];
          this.syncSelectedWithCache();
        }
      }
    },
  },
};
</script>